import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { compose } from "recompose";
import ContactForm from "~/components/ContactForm/ContactForm";
import { REASONS_REQUIRING_ORDER_NUMBER } from "~/config/support_reasons";
import client from "~/graphql/client";
import {
  FormStatus,
  ICreateTicketProps,
  OrderNumberValidationState,
  TicketType,
  withCreateTicketMutation
} from "~/graphql/mutations/trengoTicket/createTrengoTicket";
import { VALIDATE_ORDER_NUMBER_QUERY } from "~/graphql/queries/order/validateOrderNumber";
import {
  validateOrderNumber,
  validateOrderNumberVariables
} from "~/graphql/types";
import cleanup from "~/helpers/cleanup";

import nexusClient, {
  ORDER_SHIPMENTS_QUERY
} from "~/graphql/queries/order/orderByName";
import isNexusPlatform from "~/helpers/isNexusPlatform";

export interface ITrengoTicket {
  description: string;
  email: string;
  name: string;
  orderNumber: string;
  phone: string;
  reason: string;
  subject: string;
  postalCode: string;
}

type Topic = [string, string];

interface IState {
  errorCode?: string;
  status: FormStatus;
  webshopOverride?: string;
  trengoTicket: ITrengoTicket;
  orderNumberValidationState: OrderNumberValidationState;
  isModalOpen: boolean;
}

interface IProps {
  reason?: string;
  orderNumber?: string;
  description?: string;
}
class ContactFormContainer extends React.Component<
  IProps & ICreateTicketProps & WithTranslation & RouteComponentProps<{}>,
  IState
> {
  public static getDerivedStateFromProps(
    props: IProps & ICreateTicketProps & RouteComponentProps<{}>,
    state: IState
  ): IState {
    const { location } = props;
    const queryParameters = new URLSearchParams(location.search);
    const webshopOverride = queryParameters.get("webshop") || undefined;

    const { trengoTicket } = state;

    return {
      ...state,
      trengoTicket: {
        ...trengoTicket,
        description:
          (trengoTicket.description !== "" && trengoTicket.description) ||
          props.description ||
          "",
        orderNumber:
          (trengoTicket.orderNumber !== "" && trengoTicket.orderNumber) ||
          props.orderNumber ||
          "",
        reason:
          (trengoTicket.reason !== "" && trengoTicket.reason) ||
          props.reason ||
          (queryParameters.get("source") === "Delivery time exceeded" &&
            "Delivery time exceeded") ||
          ""
      },
      webshopOverride
    };
  }

  public state: IState = {
    isModalOpen: false,
    orderNumberValidationState: OrderNumberValidationState.DEFAULT,
    status: FormStatus.FORM,
    trengoTicket: {
      description: "",
      email: "",
      name: "",
      orderNumber: "",
      phone: "",
      postalCode: "",
      reason: "",
      subject: ""
    }
  };

  public render() {
    const { location } = this.props;
    const queryParameters = new URLSearchParams(location.search);

    const {
      trengoTicket,
      errorCode,
      status,
      orderNumberValidationState,
      isModalOpen
    } = this.state;

    switch (status) {
      case FormStatus.FORM:
      case FormStatus.FORM_LOADING:
        return (
          <ContactForm
            source={queryParameters.get("source")}
            loading={status === FormStatus.FORM_LOADING}
            errorCode={errorCode}
            trengoTicket={trengoTicket}
            onSubmit={this.handleSubmit}
            onChange={this.handleChange}
            onValidateOrderNumber={this.handleValidateOrderNumber}
            orderNumberValidationState={orderNumberValidationState}
            isModalOpen={isModalOpen}
            closeModal={this.handleCloseModal}
          />
        );
    }
  }

  public componentDidMount() {
    this.setState(state => {
      return {
        trengoTicket: {
          ...state.trengoTicket,
          ...this.getSubjectForReason(state.trengoTicket.reason)
        }
      };
    });
  }

  public componentDidUpdate(prevProps: IProps, prevState: IState) {
    if (
      prevState.trengoTicket.reason !== this.state.trengoTicket.reason &&
      this.state.trengoTicket.reason === "RMA request"
    ) {
      this.props.history.push("/rma/request?notify=rma_redirect");
      return;
    }

    if (prevState.trengoTicket.reason === this.state.trengoTicket.reason) {
      return;
    }

    if (
      prevState.trengoTicket.reason !== this.state.trengoTicket.reason &&
      this.state.trengoTicket.reason === "Where is my order"
    ) {
      this.setState({ isModalOpen: true });
    }

    this.setState(state => {
      return {
        trengoTicket: {
          ...state.trengoTicket,
          ...this.getSubjectForReason(state.trengoTicket.reason)
        }
      };
    });
  }

  private handleValidateOrderNumber = async () => {
    const { orderNumber, postalCode } = this.state.trengoTicket;

    if (!orderNumber || !postalCode) {
      return;
    }

    if (isNexusPlatform) {
      try {
        this.setState({
          orderNumberValidationState: OrderNumberValidationState.LOADING
        });

        await nexusClient.query({
          fetchPolicy: "network-only",
          query: ORDER_SHIPMENTS_QUERY,
          variables: {
            name: orderNumber,
            zip: postalCode
          }
        });

        this.setState({
          orderNumberValidationState: OrderNumberValidationState.VALID
        });
      } catch {
        this.setState({
          orderNumberValidationState: OrderNumberValidationState.INVALID
        });
      }
    } else {
      if (cleanup(orderNumber).match(/^BVB\d{3,15}$/i)) {
        this.setState({
          orderNumberValidationState: OrderNumberValidationState.VALID
        });

        return;
      }

      this.setState({
        orderNumberValidationState: OrderNumberValidationState.LOADING
      });

      const response = await client.query<
        validateOrderNumber,
        validateOrderNumberVariables
      >({
        fetchPolicy: "network-only",
        query: VALIDATE_ORDER_NUMBER_QUERY,
        variables: {
          orderNumber: cleanup(orderNumber)
        }
      });

      if (response.data.valid) {
        this.setState({
          orderNumberValidationState: OrderNumberValidationState.VALID
        });
      } else {
        this.setState({
          orderNumberValidationState: OrderNumberValidationState.INVALID
        });
      }
    }
  };

  private handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const { trengoTicket, orderNumberValidationState } = this.state;

    if (
      REASONS_REQUIRING_ORDER_NUMBER.includes(trengoTicket.reason) &&
      (!trengoTicket.orderNumber ||
        orderNumberValidationState === OrderNumberValidationState.INVALID)
    ) {
      this.setState({ errorCode: "enterRequiredFields" });

      return;
    }

    if (
      !(
        trengoTicket.name &&
        trengoTicket.email &&
        trengoTicket.reason &&
        trengoTicket.description
      )
    ) {
      this.setState({ errorCode: "enterRequiredFields" });

      return;
    }

    this.setState({
      status: FormStatus.FORM_LOADING
    });

    try {
      await this.props.createTicket({
        variables: {
          input: {
            description: trengoTicket.description,
            email: trengoTicket.email,
            ...(trengoTicket.orderNumber !== "" && {
              orderName: trengoTicket.orderNumber
            }),
            subject: trengoTicket.subject,
            submitter: {
              name: trengoTicket.name,
              phone: trengoTicket.phone
            },
            type: TicketType.CONTACT_REQUEST,
            ...(trengoTicket.postalCode !== "" && {
              zip: trengoTicket.postalCode
            })
          }
        }
      });

      this.props.history.push("/contact/thank-you");
    } catch (error) {
      this.setState({ errorCode: "network_error", status: FormStatus.FORM });
    }
  };

  private handleChange = (changes: Partial<ITrengoTicket>) => {
    this.setState(prevState => {
      return {
        trengoTicket: { ...prevState.trengoTicket, ...changes }
      };
    });

    const {
      trengoTicket: { name, email, reason, description },
      errorCode,
      orderNumberValidationState
    } = this.state;

    if (
      errorCode !== "" &&
      name &&
      email &&
      reason &&
      description &&
      (REASONS_REQUIRING_ORDER_NUMBER.includes(reason)
        ? orderNumberValidationState === OrderNumberValidationState.VALID
        : true)
    ) {
      this.setState({ errorCode: "" });
    }
  };

  private handleCloseModal = () => {
    this.setState({ isModalOpen: false });
  };

  private getSubjectForReason(givenReason: string): Partial<ITrengoTicket> {
    const topics: Topic[] = this.props.t("inputs.subject.options", {
      returnObjects: true
    });

    const option = topics.find(([key]) => key === givenReason);

    if (option) {
      const [reason, subject] = option;

      return { subject, reason };
    } else {
      return {};
    }
  }
}

export default compose<{}, IProps>(
  withCreateTicketMutation,
  withTranslation("contactForm"),
  withRouter
)(ContactFormContainer);



// WEBPACK FOOTER //
// ./src/containers/ContactForm/ContactFormContainer.tsx